import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { BarChart3, Search, Target, Stethoscope, GraduationCap, ExternalLink, Cpu } from 'lucide-react';

const accent = '#d4af37';
const navy = '#1a2332';
const RIQ_URL = 'https://therecruiteriq.com';

const features = [
  { icon: BarChart3, name: 'Market Intelligence', desc: 'AI-powered market insights, salary benchmarks, competitive landscape analysis, and sourcing strategies.' },
  { icon: Search, name: 'Advanced AI Sourcing', desc: 'Chat with AI to find 25+ candidates instantly using the OpenAI o1 reasoning model, with multi-select and SmartMatch integration.' },
  { icon: Target, name: 'SmartMatch', desc: 'AI candidate scoring (0–10) with technical and cultural fit analysis across resumes and LinkedIn, plus instant rankings.' },
  { icon: Stethoscope, name: 'Sourcing Doctor', desc: 'An AI sourcing expert that diagnoses challenges and prescribes solutions — including Boolean search generation and channel recommendations.' },
  { icon: GraduationCap, name: 'TA Certifications', desc: 'Industry-recognized Bronze, Silver, and Gold certifications with a comprehensive curriculum and LinkedIn-shareable badges.' },
];

const stats = [
  { value: '1M+', label: 'TA Jobs' },
  { value: '6+', label: 'AI Models' },
  { value: '10K+', label: 'Recruiters' },
  { value: '3', label: 'Certifications' },
];

const RecruiterIQPage = () => {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      '@id': 'https://therecruiteriq.com/#softwareapplication',
      name: 'RecruiterIQ',
      url: RIQ_URL,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: 'AI-powered recruitment intelligence and talent sourcing platform offering market intelligence, AI candidate sourcing, SmartMatch scoring, an AI Sourcing Doctor, and TA certifications. Trusted by 10,000+ recruiters worldwide.',
      author: { '@id': 'https://venkatswaroop.in/#person' },
      creator: { '@id': 'https://venkatswaroop.in/#person' }
    };
    let s = document.getElementById('ld-riq');
    if (!s) {
      s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = 'ld-riq';
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(schema);
    return () => { const el = document.getElementById('ld-riq'); if (el) el.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="recruiteriq-page">
      <SEOHead
        title="RecruiterIQ — AI Recruitment Intelligence Platform | Founded by Venkat Swaroop"
        description="RecruiterIQ is an AI-powered recruitment intelligence and talent sourcing platform founded by Venkat Swaroop. Features market intelligence, AI sourcing, SmartMatch scoring, Sourcing Doctor, and TA certifications — trusted by 10,000+ recruiters."
        url="https://venkatswaroop.in/recruiteriq"
      />
      <Header />

      <section className="pt-32 pb-16 text-white" style={{ backgroundColor: navy }}>
        <div className="container mx-auto px-6 text-center">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full px-4 py-1 text-sm font-semibold" style={{ backgroundColor: `${accent}26`, color: accent }}>
            <Cpu className="h-4 w-4" /> Technology &amp; Innovation
          </span>
          <h1 className="mb-4 font-serif text-4xl font-bold sm:text-5xl">RecruiterIQ</h1>
          <div className="mx-auto h-1 w-24 rounded" style={{ backgroundColor: accent }}></div>
          <p className="mx-auto mt-6 max-w-2xl text-base text-white/80 md:text-lg">
            An AI-powered recruitment intelligence &amp; talent sourcing platform — founded by Venkat Swaroop.
            From market intelligence to candidate matching, everything recruiters need to make smarter, faster
            hiring decisions with AI.
          </p>
          <a
            href={RIQ_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3 font-semibold transition-all hover:scale-105"
            style={{ backgroundColor: accent, color: navy }}
            data-testid="visit-recruiteriq-btn"
          >
            Visit RecruiterIQ <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b py-12" style={{ borderColor: `${accent}30` }}>
        <div className="container mx-auto grid max-w-4xl grid-cols-2 gap-8 px-6 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="font-serif text-4xl font-bold" style={{ color: accent }}>{s.value}</p>
              <p className="mt-1 text-sm font-medium" style={{ color: navy }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-16">
        <div className="container mx-auto max-w-6xl px-6">
          <h2 className="mb-10 text-center font-serif text-3xl font-bold" style={{ color: navy }}>
            Everything You Need to Win Talent
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.name} className="rounded-xl border p-6 transition-all hover:shadow-lg" style={{ borderColor: `${accent}40` }} data-testid={`riq-feature-${f.name.replace(/\s+/g, '-').toLowerCase()}`}>
                <span className="mb-4 inline-flex rounded-full p-3" style={{ backgroundColor: `${accent}20` }}>
                  <f.icon className="h-7 w-7" style={{ color: accent }} />
                </span>
                <h3 className="mb-2 font-serif text-xl font-bold" style={{ color: navy }}>{f.name}</h3>
                <p className="text-base leading-relaxed text-gray-700">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Builder note */}
      <section className="py-12" style={{ backgroundColor: '#f8f7f4' }}>
        <div className="container mx-auto max-w-3xl px-6 text-center">
          <h2 className="mb-4 font-serif text-2xl font-bold" style={{ color: navy }}>A technology builder, not just a leader</h2>
          <p className="text-base leading-relaxed text-gray-700">
            Beyond RecruiterIQ, Venkat Swaroop has championed the development of internal tools and AI-driven
            applications at TekWissen to streamline and modernize talent operations — reflecting a leadership
            philosophy that pairs operational discipline with intelligent, product-led innovation.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RecruiterIQPage;
